<template>
  <Dialog
    :visible="modelValue"
    modal
    header="Link Student to Request"
    class="w-full max-w-md"
    :draggable="false"
    @update:visible="emit('update:modelValue', $event)"
  >
    <div class="space-y-3 pt-2">
      <p class="text-sm text-gray-600">
        Select the verified student record that matches this applicant. Once linked, transcript data will be
        generated automatically.
      </p>

      <StudentSearchInput
        v-model="selectedStudent"
        label="Student"
        placeholder="Search by name or matric number..."
        data-testid="link-student-search"
      />

      <div v-if="selectedStudent" class="bg-gray-50 rounded p-3 text-sm">
        <div class="font-medium">{{ selectedStudent.first_name }} {{ selectedStudent.surname }}</div>
        <div v-if="selectedStudent.matric_no" class="text-xs text-gray-500">Matric: {{ selectedStudent.matric_no }}</div>
      </div>
    </div>

    <template #footer>
      <Button size="small" label="Cancel" severity="secondary" text data-testid="link-cancel" @click="close" />
      <Button size="small"
        label="Link Student"
        :loading="linkingStudent"
        :disabled="linkingStudent || !selectedStudent"
        data-testid="link-confirm"
        @click="submit"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import StudentSearchInput from '@/components/common/StudentSearchInput.vue';
import { useFormErrors } from '@/composables/useFormErrors';
import { useTranscriptMutations } from '@/composables/transcript/useTranscriptMutations';
import type { Student } from '@/types/composition-functions';

const props = defineProps<{
  modelValue: boolean;
  requestId: string | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  linked: [];
}>();

const selectedStudent = ref<Student | null>(null);
const { handleServerErrors, clearErrors } = useFormErrors();
const { linkApplicantToStudent, linkingStudent } = useTranscriptMutations();

watch(() => props.modelValue, (open) => {
  if (open) {
    selectedStudent.value = null;
    clearErrors();
  }
});

const close = () => emit('update:modelValue', false);

const submit = async() => {
  if (!props.requestId || !selectedStudent.value?.id) return;
  clearErrors();
  try {
    await linkApplicantToStudent(props.requestId, selectedStudent.value.id);
    emit('linked');
    close();
  } catch (err) {
    handleServerErrors(err);
  }
};
</script>
